import type { AttentionCode, AttentionFlag, AttentionSection, NeedsAttention } from "@fieldpath/shared";
import { ATTENTION_CRITERIA, ATTENTION_HORIZONS, mdy, mdyWeekday } from "@fieldpath/shared";
import { useEffect, useState } from "react";

import { api, ApiError, type Fetcher } from "../api/client";
import { useRefreshOnReturn } from "../refreshOnReturn";
import { navigate } from "../router";
import { todayLocal } from "./DateHint";
import { HelpButton } from "./HelpButton";
import { KindTag } from "./KindTag";

/**
 * NEEDS ATTENTION (A.1, Jeremy 13 Oct 2026; mockup reviewed and agreed the
 * same day). Problems and concerns across the work a person manages, grouped
 * by the criterion that raised them, each saying what is wrong in a sentence
 * and where it is fixed.
 *
 * **A face, no rules.** Which flags exist, and what each says, is the
 * server's (`db/needs-attention.ts`); the criteria's names and rules are the
 * shared `ATTENTION_CRITERIA`. This lays them out and filters by text.
 *
 * **Only what is wrong is shown.** A criterion with nothing on it is not
 * drawn: a page of twenty-two headings saying "none" is a page people stop
 * reading, which is the failure every attention surface here guards against.
 *
 * **Every group starts COLLAPSED** (Jeremy, 13 Oct): the headings and their
 * counts are the at-a-glance read; the rows are opened on purpose, with a
 * Show / Hide control big enough to be seen as one.
 *
 * **It fixes nothing.** Every button goes to the screen where the fix is
 * made — the task, the deliverable, Workload — so a rule is applied in one
 * place, the place it is enforced.
 */

const SECTIONS: { key: AttentionSection; title: string; blurb: string }[] = [
  { key: "problem", title: "Problems", blurb: "Fix these and the work can move again." },
  { key: "overdue", title: "Concerns · overdue now", blurb: "Already late, at every level." },
  { key: "ahead", title: "Concerns · coming up", blurb: "Not late yet. Where it could go wrong." },
];

/** The button's words: what you go there to do. */
const ACTION: Partial<Record<AttentionCode, string>> = {
  P1: "Assign",
  P2: "Open task",
  P3: "Fill role",
  P4: "Plan stages",
  P5: "Link it",
  P6: "Fill in",
  P7: "Reassign",
  P8: "Decide",
  P10: "Decide",
  P9: "Open task",
  F1: "Reassign",
  F2: "Move date",
  F5: "Move date",
};

function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0]!.toUpperCase())
    .slice(0, 2)
    .join("");
}

function todayLine(): string {
  return mdyWeekday(todayLocal()).toUpperCase();
}

export function NeedsAttentionPage({ fetcher, isAdmin }: { fetcher: Fetcher; isAdmin: boolean }) {
  const [scope, setScope] = useState<"mine" | "all">("mine");
  const [horizon, setHorizon] = useState<number>(10);
  const [query, setQuery] = useState("");
  // Collapsed by default: only the groups opened are in here.
  const [opened, setOpened] = useState<ReadonlySet<AttentionCode>>(new Set());
  const [data, setData] = useState<NeedsAttention | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Read again quietly on coming back to the tab (punch list F172).
  const back = useRefreshOnReturn();
  useEffect(() => {
    let live = true;
    setError(null);
    api
      .needsAttention(fetcher, horizon, scope)
      .then((d) => live && setData(d))
      .catch((e: unknown) => live && setError(e instanceof ApiError ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, [fetcher, horizon, scope, back]);

  const q = query.trim().toLowerCase();
  const matches = (f: AttentionFlag) =>
    q === "" ||
    [f.name, f.projectName, f.deliverableName, f.clientName, f.what, ...f.people].some(
      (v) => v !== null && v.toLowerCase().includes(q),
    );
  const flags = (data?.flags ?? []).filter(matches);
  const sectionOf = (code: AttentionCode) => ATTENTION_CRITERIA.find((c) => c.code === code)!.section;
  const count = (s: AttentionSection) => flags.filter((f) => sectionOf(f.code) === s).length;

  const open = (f: AttentionFlag) => {
    if (f.subject === "person") navigate({ kind: "workload" });
    else if (f.taskId) navigate({ kind: "task", id: f.taskId });
    else if (f.deliverableId) navigate({ kind: "deliverable", id: f.deliverableId });
  };
  const toggle = (code: AttentionCode) =>
    setOpened((c) => {
      const next = new Set(c);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });

  return (
    <div className="attn">
      <div className="attn-head">
        <div>
          <div className="today-line">{todayLine()}</div>
          <h1>
            Needs attention
            <HelpButton screens="attention" />
          </h1>
          <p className="attn-intro">
            {scope === "mine"
              ? "Across the work you manage: the project's PM or TM, the deliverable's PM or TM, or the task's owner."
              : "Across everything in Fieldpath."}{" "}
            Problems stop work finishing or moving on. Concerns put a client date at risk.
          </p>
        </div>
        {isAdmin ? (
          <div className="view-switch" role="group" aria-label="Whose work">
            <button className={scope === "mine" ? "on" : ""} onClick={() => setScope("mine")}>
              Work I manage
            </button>
            <button className={scope === "all" ? "on" : ""} onClick={() => setScope("all")}>
              Everything
            </button>
          </div>
        ) : null}
      </div>

      <div className="attn-tiles">
        <div className="attn-tile">
          <span className="attn-tile-label late">Problems</span>
          <span className="attn-tile-count">{data ? count("problem") : "…"}</span>
          <span className="attn-tile-sub">Work that cannot finish or move on as it stands</span>
        </div>
        <div className="attn-tile">
          <span className="attn-tile-label late">Overdue now</span>
          <span className="attn-tile-count">{data ? count("overdue") : "…"}</span>
          <span className="attn-tile-sub">Client dates, tasks and stages already late</span>
        </div>
        <div className="attn-tile">
          <span className="attn-tile-label soon">Coming up</span>
          <span className="attn-tile-count">{data ? count("ahead") : "…"}</span>
          <span className="attn-tile-sub">Risks in the next {horizon} working days</span>
        </div>
      </div>

      <div className="attn-tools">
        <div className="attn-search">
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
            <circle cx="7" cy="7" r="4.5" />
            <path d="M10.5 10.5l3.5 3.5" />
          </svg>
          <input
            aria-label="Filter by person, client, project, deliverable or task"
            placeholder="Filter by person, client, project, deliverable or task"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="attn-horizon" role="group" aria-label="Look ahead">
          <span>Look ahead</span>
          {ATTENTION_HORIZONS.map((n) => (
            <button key={n} className={horizon === n ? "on" : ""} aria-pressed={horizon === n} onClick={() => setHorizon(n)}>
              {n} days
            </button>
          ))}
        </div>
      </div>

      {error ? <div className="error">{error}</div> : null}
      {data?.notChecked.map((n) => (
        <p key={n} className="cal-warn">
          {n}
        </p>
      ))}
      {!data && !error ? <p className="muted">Looking…</p> : null}

      {data
        ? SECTIONS.map((s) => {
            const criteria = ATTENTION_CRITERIA.filter((c) => c.section === s.key)
              .map((c) => ({ c, rows: flags.filter((f) => f.code === c.code) }))
              .filter((g) => g.rows.length > 0);
            return (
              <section key={s.key} className="attn-section">
                <div className={`attn-section-head ${s.key}`}>
                  <h2>{s.title}</h2>
                  <span>{s.blurb}</span>
                </div>
                {criteria.length === 0 ? (
                  <p className="muted attn-none">{q ? "Nothing here matches the filter." : "Nothing here."}</p>
                ) : (
                  criteria.map(({ c, rows }) => {
                    const isOpen = opened.has(c.code);
                    return (
                      <div key={c.code} className="attn-group">
                        <button
                          type="button"
                          className="attn-group-head"
                          aria-expanded={isOpen}
                          onClick={() => toggle(c.code)}
                        >
                          <span className="attn-group-title">
                            <span className="attn-code">{c.code}</span>
                            <span className="attn-name">{c.name}</span>
                            <span className="attn-rule">{c.rule}</span>
                          </span>
                          <span className="attn-group-count">
                            <span className={`attn-pill ${s.key}`}>{rows.length}</span>
                            <span className={`attn-expand${isOpen ? " open" : ""}`} aria-hidden="true">
                              {isOpen ? "Hide" : "Show"}
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M4 6l4 4 4-4" />
                              </svg>
                            </span>
                          </span>
                        </button>
                        {isOpen
                          ? rows.map((f, i) => (
                              <div key={`${f.code}-${f.taskId ?? f.deliverableId ?? f.name}-${i}`} className="attn-row">
                                <span className="attn-item">
                                  <span className="attn-item-name">
                                    {f.subject === "deliverable" ? (
                                      <KindTag kind="deliverable" size="row" />
                                    ) : f.subject === "task" ? (
                                      <KindTag kind="task" size="row" />
                                    ) : f.subject === "stage" ? (
                                      <span className="cal-stage-tag">STG</span>
                                    ) : null}
                                    <span>{f.name}</span>
                                  </span>
                                  {f.projectName ? (
                                    <span className="attn-where">
                                      {f.projectName}
                                      {f.deliverableName && f.subject !== "deliverable" ? ` › ${f.deliverableName}` : ""}
                                      {f.clientName ? (
                                        <>
                                          {" · "}
                                          <span className="client-name">{f.clientName}</span>
                                        </>
                                      ) : null}
                                    </span>
                                  ) : null}
                                </span>
                                <span className="attn-what">{f.what}</span>
                                <span className="attn-who">
                                  {f.people.length > 0 ? (
                                    <>
                                      <span className="avatar">{initials(f.people[0]!)}</span>
                                      <span>{f.people.join(", ")}</span>
                                    </>
                                  ) : (
                                    <span className="muted">Nobody</span>
                                  )}
                                </span>
                                <span className={`attn-date${s.key === "overdue" ? " late" : ""}`}>
                                  {f.date ? mdy(f.date) : "—"}
                                </span>
                                <button type="button" className="attn-go" onClick={() => open(f)}>
                                  {f.subject === "person"
                                    ? "Workload"
                                    : (ACTION[f.code] ?? (f.taskId ? "Open task" : "Open deliverable"))}
                                </button>
                              </div>
                            ))
                          : null}
                      </div>
                    );
                  })
                )}
              </section>
            );
          })
        : null}
    </div>
  );
}
